import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import _classes from './AccordionMultiSelect.module.scss';
import { GlobalIcon, IconImage } from 'src/modules/global';
import { Collapse } from 'antd';
import { FormattedMessage } from 'react-intl';

export type AccordionMultiSelectVariant = 'default' | 'retailerMap';

export interface AccordionMultiSelectFilterData {
  id: string;
  name: string;
}

const getClasses = (variant: AccordionMultiSelectVariant): Record<string, string> => {
  if (variant === 'retailerMap') {
    return {
      ..._classes,
      collapseContainer: classnames(
        _classes.collapseContainer,
        _classes.collapseContainer_retailerMap
      ),
      filterButton: classnames(_classes.filterButton, _classes.filterButton_retailerMap),
      panelHeader: classnames(_classes.panelHeader, _classes.panelHeader_retailerMap),
      collapsePanelButtonIcon: classnames(
        _classes.collapsePanelButtonIcon,
        _classes.collapsePanelButtonIcon_retailerMap
      ),
    };
  }

  return _classes;
};

export const AccordionMultiSelect = ({
  filters,
  titleKey,
  value: selectedValues = [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange = () => {},
  variant = 'default',
  resultsCount,
}: {
  filters: AccordionMultiSelectFilterData[];
  titleKey: string;
  value?: AccordionMultiSelectFilterData[];
  onChange?: (value: AccordionMultiSelectFilterData[]) => void;
  variant?: AccordionMultiSelectVariant;
  resultsCount?: number | string;
}) => {
  const isSelected = useCallback(
    (value: AccordionMultiSelectFilterData) => {
      return !!selectedValues.find((f) => f.id === value.id);
    },
    [selectedValues]
  );

  const toggleFilterValue = useCallback(
    (value: AccordionMultiSelectFilterData) => {
      const newValues = isSelected(value)
        ? selectedValues.filter((v) => v.id !== value.id)
        : [...selectedValues, value];
      onChange && onChange(newValues);
    },
    [isSelected, onChange, selectedValues]
  );

  const classes = getClasses(variant);

  const [collapseIsActive, setCollapseIsActive] = useState(
    variant === 'retailerMap' ? true : false
  );

  const headerDefault = (
    <div className={classes.panelHeader}>
      <FormattedMessage id={titleKey} />
      {!!selectedValues.length && ` (${selectedValues.length})`}
      <GlobalIcon
        image={IconImage.DownArrow}
        className={classnames(
          classes.collapsePanelButtonIcon,
          collapseIsActive && classes.collapsePanelButtonIconActive
        )}
      />
    </div>
  );

  const headerRetailerMap = (
    <div className={classes.panelHeader}>
      <div>
        <span className={classes.panelHeaderTitle_retailerMap}>
          <FormattedMessage id={titleKey} />
        </span>
        <GlobalIcon
          image={IconImage.DownArrow}
          className={classnames(
            classes.collapsePanelButtonIcon,
            collapseIsActive && classes.collapsePanelButtonIconActive
          )}
        />
      </div>
      <p className={classes.resultsCount_retailerMap}>
        {resultsCount} <FormattedMessage id="forms/filters/results" />
      </p>
    </div>
  );

  return (
    <Collapse
      accordion
      bordered={false}
      className={classes.collapseContainer}
      expandIconPosition="right"
      // opened by default for retailerMap variant
      defaultActiveKey={variant === 'retailerMap' ? '1' : undefined}
      expandIcon={({ isActive }) => {
        setCollapseIsActive(isActive);
        return <></>;
      }}>
      <Collapse.Panel
        key="1"
        collapsible="header"
        header={variant === 'retailerMap' ? headerRetailerMap : headerDefault}>
        {filters.map((filter) => {
          const itemIsSelected = isSelected(filter);

          return (
            <div
              key={filter.id}
              className={classnames(
                classes.filterButton,
                itemIsSelected && classes.filterButtonActive
              )}
              onClick={() => {
                toggleFilterValue(filter);
              }}>
              {filter.name}
              {itemIsSelected && (
                <GlobalIcon image={IconImage.Check} className={classes.filterButtonIcon} />
              )}
            </div>
          );
        })}
      </Collapse.Panel>
    </Collapse>
  );
};
